// src/BMIDashboard.js
import React, { useEffect, useState, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from './config/api';

import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import Header from './Components/Header/BlueHeader';
import Sidebar from './Components/Sidebar/Sidebar';
import SinglePillNoCircle from './Components/SinglePillNoCircle';
import Footer from './Components/Footer/Footer';

import ARRGrowthChart from './Components/ARRGrowthChart/ARRGrowthChart';
import ContractsClosedTable from './Components/ContractsClosedTable/ContractsClosedTable';
import ContractTypeDistribution from './Components/ContractTypeDistribution';
import TopBuildingsByValueTable from './Components/TopBuildingsByValueTable';
import BuildingsTableCorporate from './Components/BuildingsTable/BuildingsTableCorporate';

import { FaBuilding, FaMoneyBillWave, FaRegEdit } from 'react-icons/fa';
import { Container, Row, Col, Spinner } from 'react-bootstrap';

function BMIDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('managerToken');

  // Core state
  const [buildingsData, setBuildingsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ totalARR: 0, activeContracts: 0, lifetimeContractValue: 0 });

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [activeTab, setActiveTab] = useState('bmi');

  // Helper function to parse contractSignedDate strings
  const parseContractDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') {
      console.log('parseContractDate: Invalid date string:', dateStr);
      return null;
    }
    
    // Parse "Month Year" format (e.g., "December 2024")
    const months = {
      'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
      'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
    };
    
    const parts = dateStr.toLowerCase().trim().split(' ');
    console.log('parseContractDate: Parsing parts:', parts, 'from:', dateStr);
    
    if (parts.length === 2) {
      const month = months[parts[0]];
      const year = parseInt(parts[1]);
      
      console.log('parseContractDate: Month index:', month, 'Year:', year);
      
      if (month !== undefined && !isNaN(year)) {
        const result = new Date(year, month, 1); // Use 1st day of month
        console.log('parseContractDate: Successfully parsed to:', result);
        return result;
      }
    }
    
    console.log('parseContractDate: Failed to parse:', dateStr);
    return null;
  };

  // Sidebar tabs
  const bmiTabs = [
    { key: 'hudson', label: 'Hudson', icon: <FaBuilding /> },
    { key: 'bmi', label: 'BMI', icon: <FaBuilding /> },
    { key: 'proposals', label: 'Proposals', icon: <FaRegEdit /> },
  ];

  // Transform BMI data to be compatible with chart components
  const transformedBuildingsData = useMemo(() => {
    return buildingsData.map(building => {
      // Calculate ARR for this building (current year only)
      const pricePerYear = building.pricePerYear || [];
      const statusPerYear = building.statusPerYear || [];
      const termYears = building.termYears || 0;
      
      // Find current year based on status
      let currentYear = 0;
      for (let i = 0; i < statusPerYear.length && i < termYears; i++) {
        if (statusPerYear[i] === 'pending') {
          currentYear = i;
          break;
        } else if (statusPerYear[i] === 'complete') {
          currentYear = i + 1; // Move to next year
        }
      }
      
      // If all years are complete, use the last year
      if (currentYear >= termYears) {
        currentYear = termYears - 1;
      }
      
      // Get ARR for current year
      let buildingARR = 0;
      if (currentYear < pricePerYear.length) {
        const yearPrice = pricePerYear[currentYear];
        if (yearPrice) {
          const numericPrice = typeof yearPrice === 'string' 
            ? parseFloat(yearPrice.replace(/,/g, '')) 
            : parseFloat(yearPrice);
          if (!isNaN(numericPrice)) {
            buildingARR = numericPrice;
          }
        }
      }
      
      // Create contracts array for chart compatibility
      // For BMI, we'll use the contractType array directly
      const contracts = building.contractType || [];
      
      // Parse contractSignedDate for chart compatibility
      const contractDate = parseContractDate(building.contractSignedDate);
      console.log('Building:', building.buildingName, 'Contract Date:', building.contractSignedDate, 'Parsed:', contractDate);
      
      // Calculate renewal date by adding term years to contract signed date
      let renewalDate = null;
      if (contractDate && building.termYears) {
        const renewalYear = contractDate.getFullYear() + building.termYears;
        const renewalMonth = contractDate.getMonth();
        renewalDate = new Date(renewalYear, renewalMonth, 1);
        
        // Format as "Month Year" string
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];
        renewalDate = `${monthNames[renewalMonth]} ${renewalYear}`;
      }
      
      return {
        buildingId: building.objectId,
        name: building.buildingName,
        manager: building.managerName,
        contractValue: buildingARR,
        photoUrl: building.imageURL,
        renewalDate: renewalDate || building.contractSignedDate, // Use calculated renewal date or fallback to contract signed date
        createdAt: contractDate, // Only use parsed date, no fallback
        updatedAt: building.updatedAt,
        // Original BMI fields
        contractType: building.contractType,
        termYears: building.termYears,
        pricePerYear: building.pricePerYear,
        statusPerYear: building.statusPerYear,
        totalContractValue: building.totalContractValue,
        // Chart compatibility - use contractType directly for BMI
        contracts: contracts
      };
    });
  }, [buildingsData]);

  const handleNavigate = (key) => {
    setActiveTab(key);
    if (key === 'hudson') navigate('/corporate');
    if (key === 'proposals') navigate('/corporate/proposals');
    if (key === 'bmi') navigate('/bmi');
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('BMI Dashboard: Starting to fetch data...');
        const res = await fetch(`${API_BASE_URL}/api/bmiBuildings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const data = await res.json();
        console.log('BMI Dashboard: Full response data:', data);
        console.log('BMI Dashboard: Number of buildings:', data.length);
        if (data.length > 0) {
          console.log('BMI Dashboard: First building sample:', data[0]);
          console.log('BMI Dashboard: Available fields:', Object.keys(data[0]));
        }
        setBuildingsData(data);

        // Calculate stats with proper ARR calculation
        let totalARR = 0;
        let activeContracts = 0;
        let lifetimeContractValue = 0;
        
        data.forEach(building => {
          const pricePerYear = building.pricePerYear || [];
          const statusPerYear = building.statusPerYear || [];
          const termYears = building.termYears || 0;
          const totalContractValue = building.totalContractValue || 0;
          const contractType = building.contractType || [];
          
          // Add to lifetime contract value
          lifetimeContractValue += Number(totalContractValue) || 0;
          
          // Count total contracts in contractType array
          activeContracts += contractType.length;
          
          // Find current year based on status
          let currentYear = 0;
          for (let i = 0; i < statusPerYear.length && i < termYears; i++) {
            if (statusPerYear[i] === 'pending') {
              currentYear = i;
              break;
            } else if (statusPerYear[i] === 'complete') {
              currentYear = i + 1; // Move to next year
            }
          }
          
          // If all years are complete, use the last year
          if (currentYear >= termYears) {
            currentYear = termYears - 1;
          }
          
          // Get ARR for current year
          let buildingARR = 0;
          if (currentYear < pricePerYear.length) {
            const yearPrice = pricePerYear[currentYear];
            if (yearPrice) {
              // Convert string to number, removing commas
              const numericPrice = typeof yearPrice === 'string' 
                ? parseFloat(yearPrice.replace(/,/g, '')) 
                : parseFloat(yearPrice);
              if (!isNaN(numericPrice)) {
                buildingARR = numericPrice;
              }
            }
          }
          
          totalARR += buildingARR;
        });
        
        setStats({ totalARR, activeContracts, lifetimeContractValue });
      } catch (err) {
        console.error('BMI Dashboard fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) fetchData();
    else navigate('/login');
  }, [token, navigate]);

  // MoM Growth Caclulations
  const arrMoM = useMemo(() => {
    const monthly = {};
    transformedBuildingsData.forEach(b => {
      if (b.createdAt && b.createdAt instanceof Date && !isNaN(b.createdAt.getTime())) {
        const d = new Date(b.createdAt);
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
        console.log('ARR MoM: Processing building', b.name, 'for month', key);
        
        // Use the pre-calculated ARR from transformed data
        const buildingARR = b.contractValue || 0;
        monthly[key] = (monthly[key] || 0) + buildingARR;
      } else {
        console.log('ARR MoM: Skipping building', b.name, 'due to invalid date:', b.createdAt);
      }
    });
    console.log('ARR MoM: Monthly data:', monthly);
    const entries = Object.entries(monthly).sort(([a],[b]) => a.localeCompare(b));
    if (entries.length < 2) return { value: '0.00%', color: '#888' };
    let cum = 0;
    const cumArr = entries.map(([month, val]) => (cum += val, { month, cum }));
    const prev = cumArr[cumArr.length-2].cum;
    const curr = cumArr[cumArr.length-1].cum;
    const pct = prev === 0 ? (curr>0 ? 100 : 0) : ((curr - prev)/prev)*100;
    return { value: `${pct>=0?'+':''}${pct.toFixed(2)}%`, color: pct>0?'#28a745': pct<0?'#dc3545':'#888' };
  }, [transformedBuildingsData]);

  const contractsMoM = useMemo(() => {
    const monthly = {};
    transformedBuildingsData.forEach(b => {
      if (b.createdAt && b.createdAt instanceof Date && !isNaN(b.createdAt.getTime())) {
        const d = new Date(b.createdAt);
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
        console.log('Contracts MoM: Processing building', b.name, 'for month', key);
        // Count contracts from contractType array
        const contractCount = Array.isArray(b.contractType) ? b.contractType.length : 0;
        monthly[key] = (monthly[key] || 0) + contractCount;
      } else {
        console.log('Contracts MoM: Skipping building', b.name, 'due to invalid date:', b.createdAt);
      }
    });
    console.log('Contracts MoM: Monthly data:', monthly);
    const entries = Object.entries(monthly).sort(([a],[b]) => a.localeCompare(b));
    if (entries.length < 2) return { value: '0.00%', color: '#888' };
    let cum = 0;
    const cumArr = entries.map(([month, val]) => (cum += val, { month, cum }));
    const prev = cumArr[cumArr.length-2].cum;
    const curr = cumArr[cumArr.length-1].cum;
    const pct = prev === 0 ? (curr>0 ? 100 : 0) : ((curr - prev)/prev)*100;
    return { value: `${pct>=0?'+':''}${pct.toFixed(2)}%`, color: pct>0?'#28a745': pct<0?'#dc3545':'#888' };
  }, [transformedBuildingsData]);

  const buildingsMoM = useMemo(() => {
    const monthly = {};
    transformedBuildingsData.forEach(b => {
      if (b.createdAt && b.createdAt instanceof Date && !isNaN(b.createdAt.getTime())) {
        const d = new Date(b.createdAt);
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
        console.log('Buildings MoM: Processing building', b.name, 'for month', key);
        // Calculate lifetime contract value for this building
        const totalContractValue = Number(b.totalContractValue) || 0;
        monthly[key] = (monthly[key] || 0) + totalContractValue;
      } else {
        console.log('Buildings MoM: Skipping building', b.name, 'due to invalid date:', b.createdAt);
      }
    });
    console.log('Buildings MoM: Monthly data:', monthly);
    const entries = Object.entries(monthly).sort(([a],[b]) => a.localeCompare(b));
    if (entries.length < 2) return { value: '0.00%', color: '#888' };
    let cum = 0;
    const cumArr = entries.map(([month, val]) => (cum += val, { month, cum }));
    const prev = cumArr[cumArr.length-2].cum;
    const curr = cumArr[cumArr.length-1].cum;
    const pct = prev === 0 ? (curr>0 ? 100 : 0) : ((curr - prev)/prev)*100;
    return { value: `${pct>=0?'+':''}${pct.toFixed(2)}%`, color: pct>0?'#28a745': pct<0?'#dc3545':'#888' };
  }, [transformedBuildingsData]);

  // Filter/Sort/Paginate using transformed data for table
  const filtered = transformedBuildingsData.filter(b => {
    const s = searchTerm.toLowerCase();
    return (b.name||'').toLowerCase().includes(s) || (b.manager||'').toLowerCase().includes(s);
  });
  const sorted = [...filtered].sort((a,b) => {
    let va = a[sortColumn], vb = b[sortColumn];
    if (sortColumn==='contractValue') { va=Number(va)||0; vb=Number(vb)||0; }
    else if (sortColumn==='renewalDate') { va=Date.parse(va)||0; vb=Date.parse(vb)||0; }
    else { va=(va||'').toString().toLowerCase(); vb=(vb||'').toString().toLowerCase(); }
    return va<vb? (sortDirection==='asc'? -1:1) : va>vb? (sortDirection==='asc'?1:-1):0;
  });
  const totalPages = Math.ceil(sorted.length/rowsPerPage);
  const pageData = sorted.slice((currentPage-1)*rowsPerPage, (currentPage-1)*rowsPerPage+rowsPerPage);

  if (isLoading) return <div style={{textAlign:'center', marginTop:'3rem'}}><Spinner animation="border"/></div>;

  return (
    <div style={{ display: 'flex' }}>
      <div className="dashboard-sidebar-host" onMouseEnter={()=>setSidebarCollapsed(false)} onMouseLeave={()=>setSidebarCollapsed(true)} style={{height:'100vh'}}>
        <Sidebar activeKey={activeTab} onNavigate={handleNavigate} collapsed={sidebarCollapsed} onToggle={()=>{}} tabs={bmiTabs} />
      </div>
      <div className="dashboard-content" style={{ flex:1, marginLeft: sidebarCollapsed?'72px':'240px', transition:'margin-left 240ms' }}>
        <DashboardNavbar buildingName="BMI Overview" contractLabel="BMI Overview" />
        <Header isCorporate />

        <Container fluid style={{ width:'91.5%', margin:'20px auto' }}>
          <Row className="gx-3 gy-3">
            <Col xs={12} md={4}><SinglePillNoCircle title="Total ARR" value={`$${stats.totalARR.toLocaleString()}`} Icon={FaMoneyBillWave} iconColor="#28a745" subLabel={<MoM label="MoM % Growth" value={arrMoM}/>} /></Col>
            <Col xs={12} md={4}><SinglePillNoCircle title="Active Contracts" value={stats.activeContracts} Icon={FaBuilding} iconColor="#007bff" subLabel={<MoM label="MoM % Growth" value={contractsMoM}/>} /></Col>
            <Col xs={12} md={4}><SinglePillNoCircle title="Lifetime Contract Value" value={`$${stats.lifetimeContractValue.toLocaleString()}`} Icon={FaMoneyBillWave} iconColor="#6f42c1" subLabel={<MoM label="MoM % Growth" value={buildingsMoM}/>} /></Col>
          </Row>
        </Container>

        <Container fluid style={{ width:'91.5%', margin:'20px auto' }}>
          <Row className="gx-4">
            <Col xs={12} lg={6}><ARRGrowthChart buildingsData={transformedBuildingsData} /></Col>
            <Col xs={12} lg={6}><ContractsClosedTable buildingsData={transformedBuildingsData} /></Col>
          </Row>
          <Row className="gx-4 mt-4">
            <Col xs={12} lg={6}><ContractTypeDistribution buildingsData={transformedBuildingsData} /></Col>
            <Col xs={12} lg={6}><TopBuildingsByValueTable buildingsData={transformedBuildingsData} /></Col>
          </Row>
        </Container>

        <BuildingsTableCorporate
          pageData={pageData}
          sortedData={sorted}
          startIndex={(currentPage-1)*rowsPerPage}
          endIndex={(currentPage-1)*rowsPerPage+rowsPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={setSortColumn}
          handlePrevPage={()=>setCurrentPage(p=>Math.max(1,p-1))}
          handleNextPage={()=>setCurrentPage(p=>Math.min(totalPages,p+1))}
          handleRowsPerPageChange={e=>{setRowsPerPage(Number(e.target.value));setCurrentPage(1);}}
        />

        <Footer />
      </div>
    </div>
  );
}

function MoM({ label, value }) {
  return (
    <div style={{ textAlign:'right' }}>
      <div style={{ color:'#666', fontSize:'0.8rem', fontWeight:'500' }}>{label}</div>
      <div style={{ color:value.color, fontSize:'1rem', fontWeight:'600' }}>{value.value}</div>
    </div>
  );
}

export default BMIDashboard;
